import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

// Create a context for theme
const ThemeContext = createContext();

// Theme provider component
export const ThemeProvider = ({ children }) => {
    // Get theme from localStorage or default to light
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('app-theme');
        return savedTheme || 'dark';
    });

    // Update theme in localStorage and on body element
    useEffect(() => {
        localStorage.setItem('app-theme', theme);

        // Remove all theme classes and add the current one
        document.body.classList.remove('theme-light', 'theme-dark');
        document.body.classList.add(`theme-${theme}`);
    }, [theme]);

    // Both setters are memoised with an empty dep list so their identity is STABLE for the life of
    // the provider. This is load-bearing, not tidiness: HelpPopupButton runs
    //
    //     useEffect(() => { … setTheme(appearance) }, [appearance, setTheme])
    //
    // to apply the user's Appearance preference. With a fresh `setTheme` on every provider render,
    // changing the theme re-rendered the provider, which changed that identity, which re-ran the
    // effect, which immediately reset the theme back to the Appearance preference — so every
    // toggleTheme() in the app (PublicHeader, DocsStandaloneLayout, the Components gallery) flipped
    // the theme for one frame and snapped straight back.
    const toggleTheme = useCallback(() => {
        setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
    }, []);

    // Function to set a specific theme
    const setSpecificTheme = useCallback((newTheme) => {
        if (['light', 'dark'].includes(newTheme)) {
            setTheme(newTheme);
        }
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme: setSpecificTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

// Custom hook to use the theme context
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};